import { collection, doc, addDoc, getDoc, getDocs, updateDoc, query, where, serverTimestamp, orderBy } from "firebase/firestore";
import { db } from "./firebase";
import { COLLECTIONS } from "./collections";
import { cashierShiftConverter } from "../models/cashierShift/cashierShiftConverter";

class CashierShiftService {
    /**
     * Referência tipada para a subcoleção cashierShifts
     */
    getCollectionRef(idTenant) {
        if (!idTenant) throw new Error("idTenant é obrigatório.");
        return collection(db, "tenants", idTenant, COLLECTIONS.CASHIER_SHIFTS).withConverter(cashierShiftConverter);
    }

    canManageAllCashier(actor = {}) {
        const role = actor?.role || null;
        const permissions = actor?.permissions || {};

        return (
            role === "super_admin"
            || role === "owner"
            || role === "admin"
            || permissions.financial_cashflow === true
            || permissions.financial_config === true
        );
    }

    buildOpenShiftsQuery(idTenant, { operatorId = null, idBankAccount = null } = {}) {
        const shiftsRef = this.getCollectionRef(idTenant);
        const constraints = [where("status", "==", "OPEN")];

        if (operatorId) constraints.push(where("operatorId", "==", operatorId));
        if (idBankAccount) constraints.push(where("idBankAccount", "==", idBankAccount));

        return query(shiftsRef, ...constraints);
    }

    /**
     * Lista turnos de caixa abertos com filtros opcionais.
     */
    async getOpenShifts(idTenant, filters = {}) {
        const snapshot = await getDocs(this.buildOpenShiftsQuery(idTenant, filters));
        return snapshot.docs.map(docSnap => docSnap.data());
    }

    /**
     * Busca o turno de caixa ABERTO do operador atual.
     */
    async getMyOpenShift(idTenant, operatorId, idBankAccount = null) {
        if (!operatorId) throw new Error("operatorId é obrigatório.");
        const shifts = await this.getOpenShifts(idTenant, { operatorId, idBankAccount });
        return shifts[0] || null;
    }

    /**
     * Busca um turno específico pelo ID.
     */
    async getById(idTenant, shiftId) {
        if (!idTenant || !shiftId) throw new Error("idTenant e shiftId são obrigatórios.");
        const docRef = doc(db, "tenants", idTenant, COLLECTIONS.CASHIER_SHIFTS, shiftId).withConverter(cashierShiftConverter);
        const snapshot = await getDoc(docRef);
        return snapshot.exists() ? snapshot.data() : null;
    }

    /**
     * Lista todos os turnos de caixa fechados ou abertos registrados num dia específico
     */
    async getShiftsByDate(idTenant, dateString, { operatorId = null } = {}) {
        const shiftsRef = this.getCollectionRef(idTenant);
        const constraints = [
            where("dateString", "==", dateString),
            orderBy("openedAt", "desc")
        ];

        if (operatorId) constraints.unshift(where("operatorId", "==", operatorId));

        const q = query(shiftsRef, ...constraints);

        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => doc.data());
    }

    assertActorOwnsShift(shift, actingUser, canManageAll = false) {
        if (!shift) {
            throw new Error("Turno de caixa não encontrado.");
        }

        if (canManageAll) return;

        if (!actingUser?.uid) {
            throw new Error("Usuário autenticado é obrigatório para operar o caixa.");
        }

        if (shift.operatorId !== actingUser.uid) {
            throw new Error("Você não pode operar um caixa que pertence a outro colaborador.");
        }
    }

    /**
     * Abre um novo turno de caixa
     */
    async openShift(idTenant, { idBankAccount, operatorId, operatorName, openingBalance, actingUser, canManageAll = false }) {
        if (!operatorId) throw new Error("operatorId é obrigatório.");

        if (!canManageAll && actingUser?.uid && operatorId !== actingUser.uid) {
            throw new Error("Você só pode abrir o seu próprio caixa.");
        }

        // Regra operacional: um único caixa aberto por colaborador.
        const currentOwnOpen = await this.getMyOpenShift(idTenant, operatorId);
        if (currentOwnOpen) {
            throw new Error("Você já possui um caixa aberto. Feche-o antes de abrir outro.");
        }

        const shiftsRef = this.getCollectionRef(idTenant);
        const todayStr = new Date().toISOString().split("T")[0];

        const newShift = {
            idBankAccount,
            operatorId,
            operatorName: operatorName || actingUser?.displayName || actingUser?.fullName || actingUser?.name || actingUser?.email || "",
            status: "OPEN",
            dateString: todayStr,
            openedAt: serverTimestamp(),
            openingBalance: openingBalance || 0,
            closingBalance: null,
            systemComputedBalance: null,
            difference: null,
        };

        const docRef = await addDoc(shiftsRef, newShift);
        const createdDoc = await getDoc(docRef);
        return createdDoc.data();
    }

    /**
     * Fechamento cego de caixa
     */
    async closeShift(idTenant, shiftId, { closingBalance, systemComputedBalance, actingUser, canManageAll = false }) {
        if (!idTenant || !shiftId) throw new Error("idTenant e shiftId são obrigatórios.");

        const currentShift = await this.getById(idTenant, shiftId);
        this.assertActorOwnsShift(currentShift, actingUser, canManageAll);

        const finalClosingBalance = Number(closingBalance) || 0;
        const finalComputedBalance = Number(systemComputedBalance) || 0;
        const difference = finalClosingBalance - finalComputedBalance;
        
        // Remove withConverter para updateDoc pois é apenas uma alteração parcial
        const docRef = doc(db, "tenants", idTenant, COLLECTIONS.CASHIER_SHIFTS, shiftId);
        
        await updateDoc(docRef, {
            status: "CLOSED",
            closedAt: serverTimestamp(),
            closingBalance: finalClosingBalance,
            systemComputedBalance: finalComputedBalance,
            difference,
            updatedAt: serverTimestamp()
        });

        // Usamos doc() novamente do zero com o converter apensado, em vez de atrelar a um let/const já inicializado, pois comV9 isso pode espirrar
        const typedDocRef = doc(db, "tenants", idTenant, COLLECTIONS.CASHIER_SHIFTS, shiftId).withConverter(cashierShiftConverter);
        const updatedDoc = await getDoc(typedDocRef);
        return updatedDoc.data();
    }
}

const cashierShiftService = new CashierShiftService();
export default cashierShiftService;
